import { Carousel, Tag, Spin, Alert } from "antd";
import "antd/dist/reset.css";
import { useTranslation } from 'react-i18next';
import EmptyState from './EmptyState';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import banner from "../assets/images/banner.png";
import axiosInstance from "../services/axios";
const getImageUrl = (image, defaultFolder = 'banner') => {
  if (!image) return null;
  if (typeof image !== 'string') return null;

  if (image.startsWith('http://') || image.startsWith('https://')) {
    if (image.includes('localhost') || image.includes('127.0.0.1')) {
      const mediaIdx = image.indexOf('/media/');
      if (mediaIdx !== -1) return image.substring(mediaIdx);
    }
    if (window.location.protocol === 'https:' && image.startsWith('http:')) {
      return image.replace(/^http:/, 'https:');
    }
    return image;
  }

  if (image.startsWith('/media/')) return image;
  if (image.startsWith('/')) return `/media${image}`;
  if (image.includes('/')) return `/media/${image}`;
  return `/media/${defaultFolder}/${image}`;
};

const CarouselBanner = () => {
  const { i18n } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [annLoading, setAnnLoading] = useState(false);
  const [annError, setAnnError] = useState(null);

  // Add responsive CSS for carousel buttons and marquee animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes marquee {
        0% {
          transform: translateX(100%);
        }
        100% {
          transform: translateX(-100%);
        }
      }
      
      .animate-marquee {
        animation: marquee 20s linear infinite;
      }
      
      .animate-marquee:hover {
        animation-play-state: paused;
      }
      
      @media (max-width: 768px) {
        .carousel-buttons {
          flex-direction: column !important;
          width: 100% !important;
          max-width: 300px !important;
        }
        .carousel-button {
          width: 100% !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchBanners = async () => {

      setLoading(true);
      setError(null);
      try {
        const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
        const resp = await axiosInstance.get('/banner/', {
          params: { language: languageParam }
        });

        // normalize response (array or {results: [], data: []})
        let items = [];
        if (Array.isArray(resp.data)) items = resp.data;
        else if (resp.data && typeof resp.data === 'object') {
          items = resp.data.results || resp.data.data || null;
          if (!items) {
            const values = Object.values(resp.data);
            if (Array.isArray(values) && values.length && typeof values[0] === 'object') items = values;
          }
        }

        if (!mounted) return;
        if (Array.isArray(items)) {
          const mapped = items
            .map(it => ({
              id: it.id,
              title: it.title,
              subtitle: it.subtitle,
              sub_description: it.sub_description,
              image: getImageUrl(it.background_image || it.image, 'banner') || banner,
            }));
          setBanners(mapped);
          console.log(items[0])
        } else {
          setBanners([]);
          setError('Unexpected banner response shape');
          // eslint-disable-next-line no-console
          console.error('/banner/ returned unexpected shape', resp.data);
        }
      } catch (err) {
        setError(err.message || 'Failed to load banners');
        setBanners([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchBanners();
    return () => { mounted = false; };
  }, [i18n.language]);

  // Fetch announcements from API (prefer API over static announcement)
  useEffect(() => {
    let mounted = true;
    const fetchAnnouncements = async () => {
      setAnnLoading(true);
      setAnnError(null);
      try {
        const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
        const resp = await axiosInstance.get('/announcements/', {
          params: {  language: languageParam }
        });

        let items = [];
        if (Array.isArray(resp.data)) items = resp.data;
        else if (resp.data && typeof resp.data === 'object') {
          items = resp.data.results || resp.data.data || null;
          if (!items) {
            const values = Object.values(resp.data);
            if (Array.isArray(values) && values.length && typeof values[0] === 'object') items = values;
          }
        }

        if (!mounted) return;
        if (Array.isArray(items)) {
          const mapped = items
            .filter(it => it.is_active !== false)
            .map(it => ({
              id: it.id,
              message: it.message,
              date: it.date,
              language: it.language,
              clientId: it.clientId,
            }));
          setAnnouncements(mapped);
        } else {
          setAnnouncements([]);
          setAnnError('Unexpected announcements response shape');
          // eslint-disable-next-line no-console
          console.error('/announcements/ returned unexpected shape', resp.data);
        }
      } catch (err) {
        setAnnError(err.message || 'Failed to load announcements');
        setAnnouncements([]);
      } finally {
        if (mounted) setAnnLoading(false);
      }
    };

    fetchAnnouncements();
    return () => { mounted = false; };
  }, [i18n.language]);
  
  // If API banners have button data, use it
  // Add static buttons to the first banner
  const displayData = banners.map((item, index) => {
    const staticButtons = index === 0 ? [
      { label: 'योजना व प्रकल्प', url: '/schemes-projects', variant: 'primary' },
      { label: 'नागरिक सेवा', url: '/citizen-services', variant: 'secondary' },
    ] : [];
    
    return {
      ...item,
      sub_description: item.sub_description || '',
      buttons: index === 0 ? staticButtons : ((item.buttons && Array.isArray(item.buttons)) ? item.buttons : [])
    };
  });

  return (
    <div style={{ width: '100%', margin: '0', padding: 0 }}>
      {/* --- Carousel Section --- */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message={error} banner style={{ marginBottom: 16 }} />
      ) : displayData.length === 0 ? (
        <div style={{ padding: '100px 0', background: '#f5f5f5' }}>
          <EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
        </div>
      ) : (
        <Carousel autoplay>
          {displayData.map((item, index) => (
            <div key={index}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "400px",
                  backgroundImage: `url(${item.image})`,
                  backgroundPosition: "center",
                  backgroundSize: "100% 100%", // 👈 shows full image
                  backgroundRepeat: "no-repeat",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  textAlign: "center",
                  // some black shadow from bottom to top 
                  boxShadow: "inset 0px -100px 150px -20px rgba(0, 0, 0, 0.7)",
                  color: "#fff",
                }}
              >
                <h2
                  style={{
                    padding: "0px 20px",
                    borderRadius: "8px",
                    fontFamily: "Teko",
                    fontWeight: 500,
                    fontSize: 'clamp(32px, 5vw, 100px)', 
                    margin: 0, 
                    marginBottom: 0,
                    color: '#fff'
                  }}
                >
                  {item.title}
                </h2>
                <span style={{ fontSize: 'clamp(20px, 3.2vw, 28px)', fontWeight: 'bold', marginTop: '0px', color: '#fff' }}>
                  {item.subtitle}
                </span>
                {item.sub_description && (
                  <p style={{ fontSize: 'clamp(14px, 2vw, 20px)', marginTop: '0px', fontWeight: 'bold', color: 'var(--color-text-light)' }}>
                    {item.sub_description}
                  </p>
                )}
                <div 
                  style={{ 
                    display: 'flex', 
                    gap: 16, 
                    marginTop: 0, 
                    justifyContent: 'center', 
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    width: 'auto'
                  }}
                  className="carousel-buttons"
                >
                  {item.buttons && item.buttons.map((btn, bidx) => (
                    <Link
                      key={bidx}
                      to={btn.url}
                      style={{
                        background: btn.variant === 'primary'
                          ? 'var(--color-primary)'
                          : 'var(--color-bg-gradient)',
                        color: btn.variant === 'primary' ? 'var(--color-text-light)' : 'var(--color-text)',
                        padding: '8px 24px',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 12,
                        textDecoration: 'none',
                        border: 'none',
                        height: '32px',
                        transition: 'background 0.2s',
                        display: 'inline-block',
                        textAlign: 'center'
                      }}
                      className="carousel-button"
                      aria-label={btn.label}
                    >
                      {btn.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      )}

      <div
        role="status"
        aria-live="polite"
        style={{
          background: 'linear-gradient(135deg, #FFF7ED 0%, #FEFCE8 100%)',
          marginTop: 0,
          padding: '8px 16px',
          fontSize: 16,
          border: '1px solid #ffe1a5ff',
          color: '#333',
          textAlign: 'center',
        }}
      >
        {annLoading ? (
          <div style={{ display: 'inline-block', padding: 4 }}><Spin size="small" /></div>
        ) : annError ? (
          <Alert type="warning" message={annError} banner />
        ) : announcements.length === 0 ? (
          <EmptyState 
            description={i18n.language === 'mr' ? 'घोषणा लवकरच उपलब्ध होईल' : 'Announcements will be available soon'} 
          />
        ) : (
          <div className="overflow-hidden whitespace-nowrap">
            <div className="animate-marquee inline-block">
              <Tag
                style={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #EC5042 0%, #B7131A 100%)',
                  color: "white",
                  marginRight: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 8px',
                  minHeight: '24px',
                  lineHeight: '1'
                }}
                color="red"
              >
                {i18n.language === 'en' ? 'New Announcement:' : 'नवी घोषणा:'}
              </Tag>
              <span style={{ transform: 'translateY(2px)', display: 'inline-block' }}>
                {announcements.map(a => a.message).join('  •  ')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default CarouselBanner;